import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState("");
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([
      api.get(`/posts/${postId}`),
      api.get(`/posts/${postId}/comments`),
    ]);
    setPost(p.data);
    setComments(c.data);
  }, [postId]);

  useEffect(() => {
    load().catch(() => setPost(false));
  }, [load]);

  const act = async (path) => {
    if (!user) return navigate("/login");
    try {
      const { data } = await api.post(`/posts/${postId}/${path}`);
      setPost(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitFinish = async () => {
    const value = parseInt(days, 10);
    if (!value || value < 1 || value > 365) {
      toast.error("Enter the number of days you held it (1-365)");
      return;
    }
    try {
      const { data } = await api.post(`/posts/${postId}/finish`, { days: value });
      setPost(data);
      setDays("");
      toast.success("Logged on the leaderboard");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const comment = async (e) => {    e.preventDefault();
    if (!user) return navigate("/login");
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${postId}/comments`, { body: draft });
      setComments((c) => [...c, data]);
      setDraft("");
      setPost((p) => ({ ...p, comment_count: p.comment_count + 1 }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removePost = async () => {
    try {
      await api.delete(`/posts/${postId}`);
      toast.success("Post deleted");
      navigate("/community");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (post === false)
    return (
      <main className="min-h-screen pt-40 text-center text-white/40" data-testid="post-not-found">
        That post is gone.
      </main>
    );
  if (!post)
    return <main className="min-h-screen pt-40 text-center text-white/30">Loading…</main>;

  const isFeedback = post.kind === "feedback";
  const canDelete = user && (user.user_id === post.author_id || user.role === "admin");

  return (
    <main data-testid="post-detail-page" className="min-h-screen pt-28 pb-24">
      <div className="mx-auto w-[min(760px,calc(100%-32px))]">
        <Link to="/community" data-testid="back-to-community" className="text-xs text-white/35 hover:text-ember">
          ← Back to community
        </Link>

        <div className="flex items-center gap-3 mt-8 mb-4 flex-wrap">
          <span className="display text-[10px] tracking-[0.25em] text-forge">{post.kind}</span>
          {post.creation_type && (
            <span className="text-[11px] uppercase tracking-wider text-white/40">{post.creation_type}</span>
          )}
          {post.feedback_type && (
            <span className="text-[11px] uppercase tracking-wider text-ember">{post.feedback_type}</span>
          )}
          {isFeedback && post.status && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-white/20 text-white/60">
              {post.status}
            </span>
          )}
          <span className="text-[11px] text-white/25">{timeAgo(post.created_at)}</span>
        </div>

        <h1 data-testid="post-title" className="headline text-4xl sm:text-5xl mb-5">
          {post.title}
        </h1>

        <div className="flex items-center gap-3 mb-8 text-xs text-white/40">
          <Link to={`/u/${post.author_id}`} className="hover:text-ember">
            {post.author_name}
          </Link>
          {post.duration && <span>· {post.duration}</span>}
          {post.difficulty && <span>· {post.difficulty}</span>}
        </div>

        {post.body && (
          <p className="text-[15px] text-white/70 leading-[1.9] whitespace-pre-wrap mb-8">{post.body}</p>
        )}

        {post.image && (
          <img src={post.image} alt={post.title} className="rounded-2xl border border-white/10 mb-8 max-h-[520px]" />
        )}

        {post.contents?.length > 0 && (
          <div className="panel rounded-2xl p-6 mb-8">
            <div className="display text-[10px] tracking-[0.25em] text-white/40 mb-4">
              {post.creation_type === "pouch" ? "Dice in this pouch" : "Faces"}
            </div>
            <div className="flex flex-wrap gap-2">
              {post.contents.map((c, i) => (
                <span key={i} className="text-sm px-3 py-1.5 rounded-xl bg-white/[0.05] text-white/75">
                  {c}
                </span>
              ))}
            </div>
            <p className="text-[11px] text-white/25 mt-5">
              Rebuild this by hand in the app — shared creations are displayed only.
            </p>
          </div>
        )}

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map((t) => (
              <Link
                key={t}
                to={`/community?kind=${post.kind}&tag=${t}`}
                className="text-[11px] px-3 py-1 rounded-full border border-white/10 text-white/40 hover:text-ember hover:border-ember/40 transition-colors"
              >
                #{t}
              </Link>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 mb-12 flex-wrap">
          <button
            data-testid="like-btn"
            onClick={() => act("like")}
            className={`display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full border transition-colors ${
              post.liked ? "border-forge text-forge bg-forge/10" : "border-white/15 text-white/60 hover:text-white"
            }`}
          >
            {isFeedback ? "Upvote" : "Like"} · {post.like_count}
          </button>

          {post.kind === "challenge" && (
            <button
              data-testid="join-btn"
              onClick={() => act("join")}
              className={`display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full border transition-colors ${
                post.joined ? "border-ember text-ember bg-ember/10" : "border-white/15 text-white/60 hover:text-white"
              }`}
            >
              {post.joined ? "You're in" : "I'm in"} · {post.join_count}
            </button>
          )}
          {canDelete && (
            <button
              data-testid="delete-post-btn"
              onClick={removePost}
              className="display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full border border-white/10 text-white/35 hover:text-forge hover:border-forge/40 transition-colors"
            >
              Delete
            </button>
          )}
        </div>

        {post.kind === "challenge" && (
          <div className="panel rounded-2xl p-6 mb-12" data-testid="challenge-leaderboard">
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
              <h2 className="headline text-2xl">
                Leaderboard <span className="text-white/25">{post.finish_count}</span>
              </h2>
              <span className="text-[11px] text-white/30">Longest streak held, in days</span>
            </div>

            {post.finishers?.length > 0 ? (
              <div className="space-y-1.5 mb-6">
                {post.finishers.map((f, i) => (
                  <div
                    key={f.user_id}
                    data-testid={`leaderboard-row-${i}`}
                    className="flex items-center gap-4 border-b border-white/5 pb-1.5 text-sm"
                  >
                    <span
                      className={`display text-xs w-7 shrink-0 ${
                        i === 0 ? "text-forge" : i < 3 ? "text-ember" : "text-white/30"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Link to={`/u/${f.user_id}`} className="text-white/80 hover:text-ember truncate">
                      {f.name}
                    </Link>
                    <span className="ml-auto headline text-lg text-white">{f.days}</span>
                    <span className="text-[11px] text-white/30 w-10">days</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-white/35 mb-6">
                Nobody has finished this one yet. Be the first on the board.
              </p>
            )}

            {user ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  data-testid="finish-days-input"
                  type="number"
                  min="1"
                  max="365"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  placeholder="Days"
                  className="w-24 bg-white/[0.04] border border-white/12 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-ember"
                />
                <button
                  data-testid="finish-btn"
                  onClick={submitFinish}
                  className="display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full bg-forge text-white hover:bg-ember transition-colors"
                >
                  {post.my_days ? "Update my result" : "I finished it"}
                </button>
                {post.my_days && (
                  <button
                    data-testid="remove-finish-btn"
                    onClick={async () => {
                      const { data } = await api.delete(`/posts/${postId}/finish`);
                      setPost(data);
                      toast.success("Result removed");
                    }}
                    className="display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full border border-white/12 text-white/40 hover:text-forge transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            ) : (
              <p className="text-sm text-white/40">
                <Link to="/login" className="text-ember hover:text-forge">
                  Sign in
                </Link>{" "}
                to report your streak.
              </p>
            )}
          </div>
        )}

        <h2 className="headline text-2xl mb-6">
          Comments <span className="text-white/25">{comments.length}</span>
        </h2>
        <div className="space-y-4 mb-8" data-testid="comment-list">
          {comments.map((c) => (
            <div key={c.comment_id} className="panel rounded-2xl p-5" data-testid={`comment-${c.comment_id}`}>
              <div className="flex items-center justify-between gap-4 mb-2">
                <Link to={`/u/${c.author_id}`} className="display text-[11px] tracking-[0.2em] text-ember">
                  {c.author_name}
                </Link>
                <span className="text-[11px] text-white/25">{timeAgo(c.created_at)}</span>
              </div>
              <p className="text-sm text-white/65 whitespace-pre-wrap">{c.body}</p>
              {user && (user.user_id === c.author_id || user.role === "admin") && (
                <button
                  data-testid={`delete-comment-${c.comment_id}`}
                  onClick={async () => {
                    await api.delete(`/comments/${c.comment_id}`);
                    setComments((list) => list.filter((x) => x.comment_id !== c.comment_id));
                  }}
                  className="text-[11px] text-white/25 hover:text-forge mt-3"
                >
                  Delete
                </button>
              )}
            </div>
          ))}
          {comments.length === 0 && <p className="text-sm text-white/30">No comments yet.</p>}
        </div>

        {user ? (
          <form onSubmit={comment} className="space-y-3">
            <textarea
              data-testid="comment-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={post.kind === "challenge" ? "Report your results…" : "Add a comment…"}
              required
              className="w-full min-h-[110px] bg-white/[0.04] border border-white/12 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-ember"
            />
            <button
              data-testid="comment-submit-btn"
              disabled={busy}
              className="display text-[11px] tracking-[0.2em] px-6 py-3.5 rounded-full bg-forge text-white hover:bg-ember disabled:opacity-50 transition-colors"
            >
              {busy ? "Posting…" : "Comment"}
            </button>
          </form>
        ) : (
          <p className="text-sm text-white/40">
            <Link to="/login" className="text-ember hover:text-forge">
              Sign in
            </Link>{" "}
            to comment and vote.
          </p>
        )}
      </div>
    </main>
  );
}
